import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Search } from 'lucide-react';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { getHeaderSearchItems } from '@/components/header/header-config';
import type { HeaderMode, HeaderSearchItem, HeaderSearchGroup } from '@/types/header';

interface HeaderSearchProps {
  mode: HeaderMode;
  className?: string;
}

const SEARCH_LIMIT = 8;

function groupPriority(group: HeaderSearchGroup): number {
  if (group === 'core') return 0;
  if (group === 'commerce') return 1;
  if (group === 'analytics') return 2;
  if (group === 'settings') return 3;
  return 4;
}

export function HeaderSearch({ mode, className }: HeaderSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { t: tCommon } = useTranslation('common');
  const { t: tNav } = useTranslation('navigation');
  const { t: tAdmin } = useTranslation('admin');

  const translate = useCallback(
    (item: HeaderSearchItem): string => {
      if (item.label.namespace === 'navigation') return tNav(item.label.key);
      if (item.label.namespace === 'admin') return tAdmin(item.label.key);
      return tCommon(item.label.key);
    },
    [tAdmin, tCommon, tNav],
  );

  const groupLabel = (group: HeaderSearchGroup): string => {
    if (group === 'core') return tCommon('header_group_core');
    if (group === 'commerce') return tCommon('header_group_commerce');
    if (group === 'analytics') return tCommon('header_group_analytics');
    if (group === 'settings') return tCommon('header_group_settings');
    return tCommon('header_group_system');
  };

  const searchItems = useMemo(() => getHeaderSearchItems(mode), [mode]);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const sorted = [...searchItems].sort((a, b) => {
      const groupDiff = groupPriority(a.group) - groupPriority(b.group);
      if (groupDiff !== 0) return groupDiff;
      return a.path.localeCompare(b.path);
    });

    if (!needle) return sorted.slice(0, SEARCH_LIMIT);

    return sorted
      .filter((item) => {
        const label = translate(item).toLowerCase();
        const keywords = item.keywords.join(' ').toLowerCase();
        return label.includes(needle) || keywords.includes(needle) || item.path.includes(needle);
      })
      .slice(0, SEARCH_LIMIT);
  }, [query, searchItems, translate]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isInputLike =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;

      if (event.key === '/' && !isInputLike && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const onSubmitFirst = () => {
    const first = results[0];
    if (!first) return;
    setOpen(false);
    setQuery('');
    navigate(first.path);
  };

  const onSelect = (item: HeaderSearchItem) => {
    setOpen(false);
    setQuery('');
    navigate(item.path);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn('relative w-full', className)}>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400 dark:text-slate-500"
            strokeWidth={1.75}
          />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onSubmitFirst();
              }
            }}
            placeholder={tCommon(
              mode === 'admin'
                ? 'header_search_placeholder_admin'
                : 'header_search_placeholder_app',
            )}
            aria-label={tCommon('header_search_label')}
            className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-14 text-sm text-slate-900 placeholder:text-slate-400 transition-colors hover:border-slate-300 focus-visible:border-slate-300 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/5 dark:border-charcoal-700 dark:bg-charcoal-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:hover:border-charcoal-600 dark:focus-visible:bg-charcoal-900"
          />
          <kbd className="pointer-events-none absolute right-2.5 top-1/2 inline-flex -translate-y-1/2 items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[10px] font-medium text-slate-500 dark:border-charcoal-700 dark:bg-charcoal-800 dark:text-slate-400">
            <span className="text-[11px] leading-none">/</span>
          </kbd>
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[min(34rem,88vw)] border border-slate-200 bg-white p-0 shadow-[0_12px_32px_-12px_rgba(15,23,42,0.2),0_4px_8px_-4px_rgba(15,23,42,0.06)] dark:border-charcoal-700 dark:bg-charcoal-850"
      >
        <div className="px-3 pb-1.5 pt-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
            {tCommon('header_search_suggestions')}
          </p>
        </div>

        <div className="max-h-[360px] overflow-y-auto px-2 pb-2">
          {results.length === 0 ? (
            <div className="m-1 rounded-md border border-dashed border-slate-200 px-3 py-6 text-center text-[13px] text-slate-500 dark:border-charcoal-700 dark:text-slate-400">
              {tCommon('header_search_empty')}
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {results.map((item) => {
                const active = pathname === item.path || pathname.startsWith(`${item.path}/`);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelect(item)}
                    className={cn(
                      'group flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left transition-colors',
                      active
                        ? 'bg-slate-900 text-white dark:bg-white dark:text-charcoal-900'
                        : 'text-slate-900 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-white/5',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium">
                        {translate(item)}
                      </span>
                      <span
                        className={cn(
                          'mt-0.5 block truncate font-mono text-[11px]',
                          active
                            ? 'text-white/70 dark:text-charcoal-700'
                            : 'text-slate-500 dark:text-slate-400',
                        )}
                      >
                        {item.path}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide',
                        active
                          ? 'border-white/30 text-white/80 dark:border-charcoal-700/40 dark:text-charcoal-700'
                          : 'border-slate-200 text-slate-500 dark:border-charcoal-700 dark:text-slate-400',
                      )}
                    >
                      {groupLabel(item.group)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-3 py-2.5 dark:border-charcoal-800">
          <p className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
            {tCommon('header_search_hint')}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
